<template>
  <AppLayout
    :access-status="!currentUserChecked ? 'pending' : isAuthenticated ? 'granted' : 'blocked'"
    :warning-message="city?.isDemoMode ? 'Scarborough is in demo mode. The licenses are not real, so feel free to play with the data.' : null"
    :wide-left-bar="false"
  >
    <template #page>
      <SplashView v-if="!loaded" class="d-flex flex-column align-items-center justify-content-center flex-grow-1 gap-2">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div v-if="city">
          <CityButton center class="p-2 h2" invert-colors :city="city" :icon-size="36" :line-height="18" />
        </div>
      </SplashView>
      <div v-else-if="!city">
        City "{{ cityId }}" could not be found
      </div>
      <template v-else>
        <div class="bg-white border-bottom py-3">
          <div class="city-dashboard-container d-flex flex-row align-items-center gap-2 city-top-bar">
            <CityButton :city="city" caption="Short-Term Rental Portal" invert-colors center :icon-size="36" :line-height="18" />
            <ul class="navbar-nav ms-auto small d-flex flex-column align-items-end">
              <UserMenu />
            </ul>
          </div>
        </div>

        <div class="w-100 flex-grow-1 bg-light">
          <div class="city-dashboard-container pt-4 gap-3 d-flex flex-column">
            <CityAnalytics :key="city.id" :city-id="city.id" :city-name="city.name" :summary="summary" :summary-loading="summaryLoading" :summary-error="summaryError" @reload-summary="loadSummary(city)">
              <template #data-quality>
                <DataQualitySection v-if="showDataQuality" :metrics="summary" :loading="summaryLoading" :error="summaryError" @retry="loadSummary(city)" />
              </template>
            </CityAnalytics>
            <footer class="city-home-footer">
              <STRCopyright />
              <router-link v-if="showChangelog" :to="{name:'whats-new'}" class="city-home-changelog">What's new</router-link>
            </footer>
          </div>
        </div>
      </template>
    </template>
  </AppLayout>
</template>

<script>
import {Session} from "@/models/User";
import {canViewChangelog} from '@/changelog';
import AppLayout from "@/components/common/AppLayout.vue";
import DataQualitySection from "@/components/dashboard/DataQualitySection.vue";
import STRCopyright from "@/homepage/STRCopyright.vue";
import CityAnalytics from "@/components/dashboard/CityAnalytics.vue";
import CityButton from "@/components/common/CityButton.vue";
import SplashView from "@/components/common/SplashView.vue";
import UserMenu from "@/components/common/UserMenu.vue";
import {cityRepository} from "@/data";

export default {
  components: {UserMenu, SplashView, CityButton, DataQualitySection, STRCopyright, CityAnalytics, AppLayout},
  props: {
    cityId: String,
    viewMode: String
  },
  data() {
    return {
      Session,
      city: null,
      summary: null,
      summaryLoading: false,
      summaryError: null,
      summaryRequest: 0,
    };
  },
  watch: {
    cityId: {
      immediate: true,
      async handler() {
        const cityId = this.cityId;
        this.city = null;
        this.summary = null;
        ++this.summaryRequest;
        this.summaryError = null;
        this.loading = true;
        try {
          const city = await cityRepository.loadCity(cityId, true);
          if (this.cityId !== cityId) return;
          this.city = city;
          if (city) this.loadSummary(city);
        } finally {
          this.loading = false;
        }
      }
    }
  },
  methods: {
    async loadSummary(city) {
      const request = ++this.summaryRequest;
      this.summaryLoading = true;
      this.summaryError = null;
      try {
        const summary = await city.fetchSummary();
        if (request === this.summaryRequest && this.cityId === city.id) this.summary = summary;
      } catch (error) {
        if (request === this.summaryRequest && this.cityId === city.id) this.summaryError = 'Current license activity could not be loaded.';
      } finally {
        if (request === this.summaryRequest && this.cityId === city.id) this.summaryLoading = false;
      }
    }
  },
  computed: {
    showChangelog() {
      return Session.currentUserChecked.value && canViewChangelog(Session.currentUser.value);
    },
    showDataQuality() {
      return Session.currentUser.value?.isAdmin || false;
    },
    loaded() {
      return Boolean(this.city);
    },
    color() {
      let color = this.city?.branding?.backgroundColor;
      return `#${color || '333333'}`;
    },
    iconStyle() {
      return {
        maxHeight: '48px',
        backgroundColor: '#ffffff',
        padding: '2px',
        border: `2px solid ${this.color}`
      };
    }
  }
}
</script>

<style>
.city-home-footer { padding: 8px 20px 28px; text-align: center; color: #6b6b6b; font-size: 12px; }
.city-home-changelog { display: inline-block; margin-top: .5rem; padding: .25rem .5rem; color: inherit; text-decoration: none; }
.city-home-changelog:hover { color: var(--bs-primary); text-decoration: underline; }
@media (pointer: coarse) { .city-home-changelog { min-height: 44px; display: inline-flex; align-items: center; } }
.city-dashboard-container {
  width: 100%;
  max-width: 1280px;
  margin-inline: auto;
  padding-inline: clamp(16px, 2.5vw, 32px);
}
img.bg {
  position: fixed;
  z-index: -6;
  /* aspect-ratio: auto; */
  width: calc(100% + 6px);
  height: calc(100% + 6px);
  object-fit: cover;
  overflow: hidden;
  filter: blur(3px);
  top: -3px;
  left: -3px;
}
</style>
